import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Match } from '../types';
import { useResponsive } from '../hooks/useResponsive';

interface ActiveMatchCardProps {
  match: Match;
  isNextMatch?: boolean;
  isCreator?: boolean;
  onDelete?: () => void;
  onLeave?: () => void;
  onTransfer?: () => void;
  actionLoading?: boolean;
}

export const ActiveMatchCard: React.FC<ActiveMatchCardProps> = ({ 
  match, 
  isNextMatch = false, 
  isCreator = false,
  onDelete,
  onLeave,
  onTransfer,
  actionLoading = false
}) => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();

  const formatDate = (dateString: string) => {
    // Crear la fecha como local para evitar problemas de zona horaria
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return timeString.substring(0, 5);
  };

  const getPlayerCount = (match: Match) => {
    return (match.players?.length || 0) + (match.substitutes?.length || 0);
  };

  const getStatusText = (match: Match) => {
    const matchDate = new Date(match.date + 'T' + match.time);
    const now = new Date();
    
    if (matchDate > now) {
      return isNextMatch ? 'PRÓXIMO PARTIDO' : 'PARTIDO PROGRAMADO';
    } else {
      return 'PARTIDO EN CURSO';
    }
  };

  const getStatusColor = (match: Match) => {
    const matchDate = new Date(match.date + 'T' + match.time);
    const now = new Date();
    
    if (matchDate > now) {
      return isNextMatch ? '#00E676' : '#FF9800';
    } else {
      return '#FF6B6B';
    }
  };

  return (
    <div
      style={{
        backgroundColor: '#23272A',
        borderRadius: '16px',
        border: '1px solid #33383B',
        padding: isMobile ? '16px' : '20px',
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? '12px' : '16px',
        minHeight: isMobile ? 'auto' : '200px',
        width: '100%',
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#2C2F33';
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '#23272A';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.25)';
      }}
    >
      {/* Header con fecha y estado */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: isMobile ? '8px' : '12px',
        gap: '12px'
      }}>
        <div style={{
          flex: 1,
          minWidth: 0
        }}>
          <div style={{
            fontSize: isMobile ? '14px' : '16px',
            fontWeight: 'bold',
            color: '#FFFFFF',
            marginBottom: '4px'
          }}>
            {formatDate(match.date)} · {formatTime(match.time)}
          </div>
          <div style={{
            fontSize: isMobile ? '12px' : '14px',
            color: '#B0BEC5',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%'
          }}>
            📍 {match.location}
          </div>
        </div>
        <span style={{
          padding: isMobile ? '4px 8px' : '6px 12px',
          borderRadius: '8px',
          fontSize: isMobile ? '11px' : '12px',
          fontWeight: 'bold',
          backgroundColor: getStatusColor(match),
          color: '#181A1B',
          flexShrink: 0,
          whiteSpace: 'nowrap'
        }}>
          {getStatusText(match)}
        </span>
      </div>

      {/* Equipos */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: isMobile ? '12px' : '16px',
        backgroundColor: '#2C2F33',
        borderRadius: '12px',
        border: '1px solid #40444B'
      }}>
        <div style={{
          textAlign: 'center',
          flex: 1,
          minWidth: 0 // Permite que el contenido se contraiga
        }}>
          <div style={{
            fontSize: isMobile ? '16px' : '18px',
            fontWeight: 'bold',
            color: '#FFFFFF',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            padding: '0 4px'
          }}>
            {match.team1Name}
          </div>
        </div>
        <div style={{
          fontSize: isMobile ? '14px' : '16px',
          fontWeight: 'bold',
          color: '#B0BEC5',
          margin: '0 12px',
          flexShrink: 0 // Evita que VS se contraiga
        }}>
          VS
        </div>
        <div style={{
          textAlign: 'center',
          flex: 1,
          minWidth: 0 // Permite que el contenido se contraiga
        }}>
          <div style={{
            fontSize: isMobile ? '16px' : '18px',
            fontWeight: 'bold',
            color: '#FFFFFF',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            padding: '0 4px'
          }}>
            {match.team2Name}
          </div>
        </div>
      </div>

      {/* Información del partido */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? '8px' : '12px'
      }}>
        {/* Precio y jugadores */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: isMobile ? '8px 12px' : '10px 16px',
          backgroundColor: '#2C2F33',
          borderRadius: '8px',
          border: '1px solid #40444B'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: isMobile ? '14px' : '16px' }}>💰</span>
            <span style={{
              fontSize: isMobile ? '13px' : '14px',
              color: '#FFFFFF',
              fontWeight: '600'
            }}>
              ${match.price}
            </span>
          </div>
          <div style={{
            fontSize: isMobile ? '13px' : '14px',
            color: '#B0BEC5',
            fontWeight: '600'
          }}>
            👥 {getPlayerCount(match)}/{match.maxTotalPlayers || match.maxPlayers * 2}
          </div>
        </div>

        {/* Rol del usuario */}
        {isCreator && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            padding: isMobile ? '8px 12px' : '10px 16px',
            backgroundColor: '#2C2F33',
            borderRadius: '8px',
            border: '1px solid #40444B'
          }}>
            <span style={{
              fontSize: isMobile ? '13px' : '14px',
              color: '#00E676',
              fontWeight: '600'
            }}>
              👑 Creador
            </span>
          </div>
        )}
      </div>

      {/* Botones de acción */}
      <div style={{
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => navigate(`/match/${match.id}`)}
          style={{
            flex: 1,
            padding: isMobile ? '10px 12px' : '12px 16px',
            backgroundColor: '#00E676',
            color: '#181A1B',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: '600',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#00C853';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#00E676';
          }}
        >
          Ver Partido
        </button>

        {isCreator ? (
          <>
            {onTransfer && (
              <button
                onClick={onTransfer}
                disabled={actionLoading}
                style={{
                  padding: isMobile ? '10px 12px' : '12px 16px',
                  backgroundColor: '#9C27B0',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: '600',
                  transition: 'background-color 0.2s',
                  opacity: actionLoading ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!actionLoading) {
                    e.currentTarget.style.backgroundColor = '#7B1FA2';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!actionLoading) {
                    e.currentTarget.style.backgroundColor = '#9C27B0';
                  }
                }}
              >
                {actionLoading ? '...' : 'Transferir'}
              </button>
            )}
            {onDelete && (
              <button
                onClick={onDelete}
                disabled={actionLoading}
                style={{
                  padding: isMobile ? '10px 12px' : '12px 16px',
                  backgroundColor: '#F44336',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: '600',
                  transition: 'background-color 0.2s',
                  opacity: actionLoading ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!actionLoading) {
                    e.currentTarget.style.backgroundColor = '#D32F2F';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!actionLoading) {
                    e.currentTarget.style.backgroundColor = '#F44336';
                  }
                }}
              >
                {actionLoading ? '...' : '🗑️ Eliminar'}
              </button>
            )}
          </>
        ) : (
          onLeave && (
            <button
              onClick={onLeave}
              disabled={actionLoading}
              style={{
                padding: isMobile ? '10px 12px' : '12px 16px',
                backgroundColor: '#FF9800',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                fontSize: isMobile ? '13px' : '14px',
                fontWeight: '600',
                transition: 'background-color 0.2s',
                opacity: actionLoading ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!actionLoading) {
                  e.currentTarget.style.backgroundColor = '#F57C00';
                }
              }}
              onMouseLeave={(e) => {
                if (!actionLoading) {
                  e.currentTarget.style.backgroundColor = '#FF9800';
                }
              }}
            >
              {actionLoading ? '...' : 'Abandonar'}
            </button>
          )
        )}
      </div>
    </div>
  );
}; 